import router from "@/router";
import * as Sentry from "@sentry/vue";
import type { App } from "vue";

const environment = import.meta.env.VITE_CURRENT_ENV.toLowerCase();

// True when the event carries at least one stack frame flagged as application
// code (`in_app`). The rrweb "nextSibling" crash throws entirely inside the
// minified @sentry/replay vendor bundle — an unmapped `<anonymous>` frame with
// no in-app frame — whereas a genuine application error touching a sibling
// chain would map to a `src/` frame. We use this to suppress only the vendor
// (Replay) noise and preserve real app errors. If frames are absent/unmapped
// the check returns false, so an ambiguous event fails toward *dropping* the
// known-vendor noise; a real app error always carries an in-app frame.
const hasInAppFrame = (event: Sentry.Event): boolean =>
    (event.exception?.values ?? []).some((value) =>
        (value.stacktrace?.frames ?? []).some((frame) => frame.in_app === true)
    );

const sentryInit = (app: App<Element>) => {
    if (import.meta.env.VITE_DISABLE_SENTRY) return;

    Sentry.init({
        environment,
        app,
        dsn: import.meta.env.VITE_SENTRY_DNS,
        beforeSend(event, hint) {
            // Defense-in-depth: drop isTrusted errors that slipped through handleError.
            // These originate from browser ProgressEvents (network failures at transport
            // level) and arrive here as Error("true") with name="isTrusted" after being
            // mangled by the handleError → handleObjectError → Object.entries chain.
            const error = hint.originalException;
            if (error instanceof Error && error.name === "isTrusted") {
                return null;
            }
            // Drop stale-chunk errors — the vite:preloadError handler in main.ts
            // already reloads the page; this is defense-in-depth.
            if (
                error instanceof TypeError &&
                (error.message.includes(
                    "Failed to fetch dynamically imported module"
                ) ||
                    error.message.includes("Importing a module script failed"))
            ) {
                return null;
            }
            // Drop the Session Replay (rrweb) "nextSibling" crash — a known bug inside
            // the bundled @sentry/replay recorder, not our code, that fires on DOM
            // mutations in busy views. High-volume noise with no user impact.
            // Gate on the *origin*: only suppress when no in-app frame is present
            // (the crash lives entirely in the vendor bundle). A `nextSibling`
            // TypeError that maps to our own `src/` code is preserved so a real
            // regression still reaches Sentry.
            if (
                error instanceof TypeError &&
                error.message.includes("nextSibling") &&
                !hasInAppFrame(event)
            ) {
                return null;
            }
            return event;
        },
        integrations: [
            new Sentry.BrowserTracing({
                // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
                tracePropagationTargets: [
                    "localhost:3000",
                    /^https:\/\/staging-opply\.netlify\.app/,
                ],
                routingInstrumentation: Sentry.vueRouterInstrumentation(router),
            }),
            new Sentry.Replay({
                networkDetailAllowUrls: [""],
                networkRequestHeaders: ["X-Custom-Header"],
                networkResponseHeaders: ["X-Custom-Header"],
                maskAllText: false,
                blockAllMedia: false,
            }),
        ],

        // Set tracesSampleRate to 1.0 to capture 100%
        // of transactions for performance monitoring.
        // We recommend adjusting this value in production
        tracesSampleRate: 1.0,

        // Capture Replay for 10% of all sessions,
        // plus for 100% of sessions with an error
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
    });
};

export default sentryInit;
